import { useState, useCallback, useRef } from 'react'
import axios from 'lib/axios'
import config from 'config'
import { accountScopedFetch } from 'lib/accountFetch'
import { registerAccountResetHook } from 'lib/accountSession'

// Prefix for system threads used for title generation
// These threads will be filtered out from the user's visible thread list
export const SYSTEM_THREAD_PREFIX = 'sys_'

const THREADS_ENDPOINT = '/api/v4/cortex/threads'

/**
 * Extracts a short, usable title from the agent's response text.
 * The agent sometimes returns a full analysis instead of a short title,
 * so we extract the first meaningful heading or line and truncate it.
 */
function extractTitle(rawText: string): string {
  const trimmed = rawText.trim()
  if (!trimmed) return ''

  // Try to find a markdown heading first (# Title or ## Title)
  const headingMatch = trimmed.match(/^#{1,3}\s+(.+)$/m)
  let title = headingMatch ? headingMatch[1] : ''

  // Fallback: use the first non-empty line
  if (!title) {
    const lines = trimmed
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
    title = lines[0] || ''
  }

  // Strip markdown formatting (bold, italic, remaining #)
  title = title
    .replace(/^#{1,3}\s+/, '')
    .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1')
    .replace(/_{1,2}([^_]+)_{1,2}/g, '$1')
    .replace(/^["']|["']$/g, '')
    .trim()

  // Truncate at word boundary if too long
  const MAX_LENGTH = 60
  if (title.length > MAX_LENGTH) {
    const truncated = title.substring(0, MAX_LENGTH)
    const lastSpace = truncated.lastIndexOf(' ')
    title = (lastSpace > 20 ? truncated.substring(0, lastSpace) : truncated) + '...'
  }

  return title
}

/* The NexaAI system thread used for title generation, cached across component unmounts. Cleared on
 * account teardown so the next account starts its own system thread instead of the previous one's. */
export const titleThreadCache: { systemThreadId: string | null; parentMessageId: string | null } = {
  systemThreadId: null,
  parentMessageId: null
}

registerAccountResetHook(() => {
  titleThreadCache.systemThreadId = null
  titleThreadCache.parentMessageId = null
})

/**
 * Standalone function to generate a title and rename a thread.
 * This runs independently of component lifecycle - it will complete even if the user navigates away.
 */
export async function generateAndSetThreadTitle(params: {
  agentRunEndpoint: string
  userId: string
  threadId: string
  userMessage: string
  assistantMessage: string
  onSuccess?: (title: string) => void
}): Promise<void> {
  const { agentRunEndpoint, userId, threadId, userMessage, assistantMessage, onSuccess } = params

  try {
    // Get or create system thread
    const systemOriginApp = `${SYSTEM_THREAD_PREFIX}${userId.replace(/-/g, '').substring(0, 12)}`

    if (!titleThreadCache.systemThreadId) {
      // Try to find existing system thread
      try {
        const response = await axios.get(THREADS_ENDPOINT, {
          params: { origin_application: systemOriginApp }
        })
        const threads = Array.isArray(response.data) ? response.data : response.data.threads || []
        if (threads.length > 0) {
          const sortedThreads = threads.sort(
            (a: { created_on: number }, b: { created_on: number }) => b.created_on - a.created_on
          )
          titleThreadCache.systemThreadId = String(sortedThreads[0].thread_id)
        }
      } catch (err) {
        console.error('[TitleGenerator] Error fetching system threads:', err)
      }

      // Create new system thread if none found
      if (!titleThreadCache.systemThreadId) {
        const response = await axios.post(THREADS_ENDPOINT, {
          origin_application: systemOriginApp
        })
        titleThreadCache.systemThreadId = String(response.data.thread_id)
        titleThreadCache.parentMessageId = null
      }
    }

    // Create summarization prompt
    const truncatedUser = userMessage.substring(0, 500)
    const truncatedAssistant = assistantMessage.substring(0, 500)
    const summarizationPrompt =
      'Generate a very short title (maximum 6 words) for the following conversation. ' +
      'Return ONLY the title text, nothing else. No quotes, no explanation, just the title.\n\n' +
      `User: ${truncatedUser}\n\n` +
      `Assistant: ${truncatedAssistant}`

    // Build request
    const requestBody = {
      messages: [{ role: 'user', content: [{ type: 'text', text: summarizationPrompt }] }],
      thread_id: titleThreadCache.systemThreadId,
      parent_message_id: titleThreadCache.parentMessageId || 0
    }

    const response = await accountScopedFetch(`${config.apiHost}${agentRunEndpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      },
      body: JSON.stringify(requestBody)
    })

    if (!response.ok || !response.body) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    // Process SSE stream
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let extractedTitle = ''
    let newParentMessageId: string | null = null
    let currentEventType: string | null = null

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEventType = line.substring(7).trim()
          continue
        }

        if (line.startsWith('data: ')) {
          const rawData = line.substring(6)
          if (rawData === '[DONE]') continue

          try {
            const data = JSON.parse(rawData)
            if (currentEventType === 'response.text.delta' && data.text) {
              extractedTitle += data.text
            }
            if (currentEventType === 'metadata' && data.metadata?.role === 'assistant' && data.metadata?.message_id) {
              newParentMessageId = String(data.metadata.message_id)
            }
          } catch {
            // Ignore parse errors
          }
          currentEventType = null
        }

        if (line === '') {
          currentEventType = null
        }
      }
    }

    if (newParentMessageId) {
      titleThreadCache.parentMessageId = newParentMessageId
    }

    // Extract a usable title from the response (handles long agent responses)
    const cleanedTitle = extractTitle(extractedTitle)

    if (!cleanedTitle) {
      console.warn('[TitleGenerator] Could not extract a valid title')
      onSuccess?.('')
      return
    }

    // Rename the thread
    await axios.post(`${THREADS_ENDPOINT}/${threadId}`, { thread_name: cleanedTitle })

    if (onSuccess) {
      onSuccess(cleanedTitle)
    }
  } catch (err) {
    console.error('[TitleGenerator] Error generating/setting title:', err)
    onSuccess?.('')
  }
}

interface UseThreadTitleGeneratorOptions {
  userId: string | null
  agentRunEndpoint: string | null
}

interface UseThreadTitleGeneratorReturn {
  generateTitle: (userMessage: string, assistantMessage: string) => Promise<string | null>
  isGenerating: boolean
}

/**
 * Hook for generating thread titles using a hidden system thread.
 *
 * This creates a separate thread with origin_application prefixed with 'sys_'
 * which will be filtered out from the user's visible thread list.
 *
 * The hook sends a summarization prompt to the agent and extracts the title
 * from the response.
 */
export function useThreadTitleGenerator(options: UseThreadTitleGeneratorOptions): UseThreadTitleGeneratorReturn {
  const { userId, agentRunEndpoint } = options

  const [isGenerating, setIsGenerating] = useState(false)
  const systemThreadIdRef = useRef<string | null>(null)
  const parentMessageIdRef = useRef<string | null>(null)

  /**
   * Get or create the system thread for title generation
   */
  const getOrCreateSystemThread = useCallback(async (): Promise<string> => {
    // Return cached thread ID if we have one
    if (systemThreadIdRef.current) {
      return systemThreadIdRef.current
    }

    // Create origin_application for system thread
    // Prefix with 'sys_' to distinguish from user threads
    const systemOriginApp = userId
      ? `${SYSTEM_THREAD_PREFIX}${userId.replace(/-/g, '').substring(0, 12)}`
      : SYSTEM_THREAD_PREFIX

    // First, try to find an existing system thread
    try {
      const response = await axios.get(THREADS_ENDPOINT, {
        params: { origin_application: systemOriginApp }
      })

      const threads = Array.isArray(response.data) ? response.data : response.data.threads || []

      if (threads.length > 0) {
        // Use the most recent system thread
        const sortedThreads = threads.sort(
          (a: { created_on: number }, b: { created_on: number }) => b.created_on - a.created_on
        )
        systemThreadIdRef.current = String(sortedThreads[0].thread_id)
        return systemThreadIdRef.current
      }
    } catch (err) {
      console.error('Error fetching system threads:', err)
    }

    // Create a new system thread
    try {
      const response = await axios.post(THREADS_ENDPOINT, {
        origin_application: systemOriginApp
      })

      systemThreadIdRef.current = String(response.data.thread_id)
      parentMessageIdRef.current = null // Reset parent message ID for new thread
      return systemThreadIdRef.current
    } catch (err) {
      console.error('Error creating system thread:', err)
      throw err
    }
  }, [userId])

  /**
   * Generate a title for a conversation based on the first exchange
   */
  const generateTitle = useCallback(
    async (userMessage: string, assistantMessage: string): Promise<string | null> => {
      if (!agentRunEndpoint || !userId) {
        console.warn('Cannot generate title: missing agentRunEndpoint or userId')
        return null
      }

      setIsGenerating(true)

      try {
        const systemThreadId = await getOrCreateSystemThread()

        // Create a summarization prompt - truncate messages to avoid token limits
        const truncatedUser = userMessage.substring(0, 500)
        const truncatedAssistant = assistantMessage.substring(0, 500)

        const summarizationPrompt =
          'Generate a very short title (maximum 6 words) for the following conversation. ' +
          'Return ONLY the title text, nothing else. No quotes, no explanation, just the title.\n\n' +
          `User: ${truncatedUser}\n\n` +
          `Assistant: ${truncatedAssistant}`

        // Build request body
        const requestBody: Record<string, unknown> = {
          messages: [
            {
              role: 'user',
              content: [{ type: 'text', text: summarizationPrompt }]
            }
          ],
          thread_id: systemThreadId,
          parent_message_id: parentMessageIdRef.current || 0
        }

        // Make the request
        let response: Response
        try {
          response = await accountScopedFetch(`${config.apiHost}${agentRunEndpoint}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'text/event-stream'
            },
            body: JSON.stringify(requestBody)
          })
        } catch (fetchError) {
          console.error('Fetch error during title generation:', fetchError)
          throw fetchError
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        if (!response.body) {
          throw new Error('No response body')
        }

        // Process the SSE stream to extract the title
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let extractedTitle = ''
        let newParentMessageId: string | null = null
        let currentEventType: string | null = null

        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            break
          }

          buffer += decoder.decode(value, { stream: true })

          // Parse SSE events - format is:
          // event: response.text.delta
          // data: {"text":"Hello"}
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            // Parse event type line
            if (line.startsWith('event: ')) {
              currentEventType = line.substring(7).trim()
              continue
            }

            // Parse data line
            if (line.startsWith('data: ')) {
              const rawData = line.substring(6)

              // Skip [DONE] marker
              if (rawData === '[DONE]') {
                continue
              }

              try {
                const data = JSON.parse(rawData)

                // Extract text content from text delta events
                if (currentEventType === 'response.text.delta' && data.text) {
                  extractedTitle += data.text
                }

                // Capture metadata for parent_message_id
                if (
                  currentEventType === 'metadata' &&
                  data.metadata?.role === 'assistant' &&
                  data.metadata?.message_id
                ) {
                  newParentMessageId = String(data.metadata.message_id)
                }
              } catch {
                // Ignore parse errors for non-JSON lines
              }

              // Reset event type after processing data
              currentEventType = null
            }

            // Empty line resets event type
            if (line === '') {
              currentEventType = null
            }
          }
        }

        // Update parent message ID for next request
        if (newParentMessageId) {
          parentMessageIdRef.current = newParentMessageId
        }

        // Extract a usable title from the response (handles long agent responses)
        const cleanedTitle = extractTitle(extractedTitle)

        if (!cleanedTitle) {
          return null
        }

        return cleanedTitle
      } catch (err) {
        console.error('Error generating title:', err)
        return null
      } finally {
        setIsGenerating(false)
      }
    },
    [agentRunEndpoint, userId, getOrCreateSystemThread]
  )

  return {
    generateTitle,
    isGenerating
  }
}
